/**
 * Utility functions for date manipulation in meal planning
 */

export interface WeekDay {
  date: Date;
  dayName: string;
  dayShort: string;
  dayNumber: number;
  isToday: boolean;
  isCurrentWeek: boolean;
}

/**
 * Get the Monday of the week containing the given date
 */
export function getWeekStart(date: Date = new Date()): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
  
  const monday = new Date(d.setDate(diff));
  monday.setHours(0, 0, 0, 0); // Set to midnight
  return monday;
}

/**
 * Get all 7 days of a week starting from Monday
 */
export function getWeekDays(weekStart: Date): WeekDay[] {
  const today = new Date();
  const currentWeekStart = getWeekStart(today);
  
  const days: WeekDay[] = [];
  
  for (let i = 0; i < 7; i++) {
    const date = new Date(weekStart);
    date.setDate(weekStart.getDate() + i);
    
    days.push({
      date,
      dayName: date.toLocaleDateString('es-ES', { weekday: 'long' }),
      dayShort: date.toLocaleDateString('es-ES', { weekday: 'short' }),
      dayNumber: date.getDate(),
      isToday: isSameDay(date, today),
      isCurrentWeek: isSameDay(weekStart, currentWeekStart),
    });
  }
  
  return days;
}

/**
 * Navigate to previous week
 */
export function getPreviousWeek(currentWeekStart: Date): Date {
  const prevWeek = new Date(currentWeekStart);
  prevWeek.setDate(currentWeekStart.getDate() - 7);
  return prevWeek;
}

/**
 * Navigate to next week
 */
export function getNextWeek(currentWeekStart: Date): Date {
  const nextWeek = new Date(currentWeekStart);
  nextWeek.setDate(currentWeekStart.getDate() + 7);
  return nextWeek;
}

/**
 * Check if two dates are the same day
 */
export function isSameDay(date1: Date, date2: Date): boolean {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

/**
 * Format date for display
 */
export function formatDate(date: Date, options?: Intl.DateTimeFormatOptions): string {
  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };
  
  return date.toLocaleDateString('es-ES', { ...defaultOptions, ...options });
}

/**
 * Format date range for week display
 */
export function formatWeekRange(weekStart: Date): string {
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);
  
  const startDay = weekStart.getDate();
  const endDay = weekEnd.getDate();
  const month = weekStart.toLocaleDateString('es-ES', { month: 'long' });
  const year = weekStart.getFullYear();
  
  if (weekStart.getMonth() === weekEnd.getMonth()) {
    return `${startDay} - ${endDay} de ${month} ${year}`;
  } else {
    const startMonth = weekStart.toLocaleDateString('es-ES', { month: 'short' });
    const endMonth = weekEnd.toLocaleDateString('es-ES', { month: 'short' });
    return `${startDay} ${startMonth} - ${endDay} ${endMonth} ${year}`;
  }
}

/**
 * Get ISO string for day (for API compatibility)
 */
export function getDayISO(date: Date): string {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

/**
 * Get current week start (Monday) as ISO string
 */
export function getCurrentWeekStartISO(): string {
  return getWeekStart().toISOString();
}

/**
 * Parse ISO date string to Date object
 */
export function parseISODate(isoString: string): Date {
  return new Date(isoString);
}

/**
 * Check if a date is in the past
 */
export function isPastDate(date: Date): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const checkDate = new Date(date);
  checkDate.setHours(0, 0, 0, 0);
  
  return checkDate < today;
}

/**
 * Get relative time description
 */
export function getRelativeWeekDescription(weekStart: Date): string {
  const currentWeekStart = getWeekStart();
  const diffTime = weekStart.getTime() - currentWeekStart.getTime();
  const diffWeeks = Math.round(diffTime / (7 * 24 * 60 * 60 * 1000));
  
  if (diffWeeks === 0) return 'Esta semana';
  if (diffWeeks === 1) return 'Próxima semana';
  if (diffWeeks === -1) return 'Semana pasada';
  if (diffWeeks > 0) return `En ${diffWeeks} semanas`;
  if (diffWeeks < 0) return `Hace ${Math.abs(diffWeeks)} semanas`;
  
  return formatWeekRange(weekStart);
}
